import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchEvent, respondToEvent, closeEvent, reopenEvent } from '../../features/events/services/eventService';
import EventDetailModal from '../../features/events/components/EventDetailModal';
import RsvpDetailsModal from '../../features/events/components/RsvpDetailsModal';
import {
  eventDtoToTimelineInfo,
  formatEventSchedule,
  formatRsvpSummary,
  getStatusLabel,
  getTypeMeta,
  getTypeTheme,
  PARTICIPANT_STATUS_BADGE,
  PARTICIPANT_STATUS_LABEL,
  resolveEventLocation,
  splitEventAttachments,
} from '../../features/events/utils/eventTimelineUtils';
import { needsRsvpDetailsOnAccept, hasEnabledRsvpFields } from '../../features/events/constants/rsvpFields';
import { isExpenseSharingEvent, isExpenseTrackingEvent } from '../../features/events/constants/expenseFields';
import EventAttachmentsDisplay from '../../features/events/components/EventAttachmentsDisplay';
import SharePersonalAmount from '../../features/events/components/SharePersonalAmount';
import PollVoteBlock from '../../features/events/components/PollVoteBlock';
import ConfirmModal from '../common/ConfirmModal';
import CreateEventWizard from '../../features/events/components/CreateEventWizard';
import FollowUpEventPrompt from '../../features/events/components/FollowUpEventPrompt';
import { isPollEvent, selectedPollOptions, followUpParticipantsFromPoll, pollDisplayName } from '../../features/events/utils/pollUtils';

const CARD_STYLES = `
  @keyframes eventCardEnter {
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }
  @keyframes eventShimmer {
    0% { background-position: 200% center; }
    100% { background-position: -200% center; }
  }
  .event-timeline-card {
    animation: eventCardEnter 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards;
  }
  .event-timeline-shimmer {
    background-size: 200% auto;
    animation: eventShimmer 5s linear infinite;
  }
`;

const LocationIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const CalendarIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const STATUS_PILL = {
  WAITING_FOR_RESPONSE: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  CONFIRMED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  IN_PROGRESS: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  COMPLETED: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  POST_COMPLETION: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  CLOSED: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  CANCELLED: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  EXPIRED: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200',
};

const getResponseOptions = (responseType) => {
  const type = (responseType || 'NONE').toUpperCase();
  if (type === 'NONE') return [];
  if (type === 'RSVP') {
    return [
      { id: 'ACCEPTED', label: 'Accept invitation', className: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
      { id: 'TENTATIVE', label: 'Maybe', className: 'bg-sky-600 hover:bg-sky-700 text-white' },
      { id: 'DECLINED', label: 'Decline', className: 'bg-white dark:bg-gray-800 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30' },
    ];
  }
  if (type === 'YES_NO_MAYBE') {
    return [
      { id: 'ACCEPTED', label: 'Yes', className: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
      { id: 'TENTATIVE', label: 'Maybe', className: 'bg-sky-600 hover:bg-sky-700 text-white' },
      { id: 'DECLINED', label: 'No', className: 'bg-white dark:bg-gray-800 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30' },
    ];
  }
  return [
    { id: 'ACCEPTED', label: 'Accept', className: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
    { id: 'TENTATIVE', label: 'Maybe', className: 'bg-sky-600 hover:bg-sky-700 text-white' },
    { id: 'DECLINED', label: 'Decline', className: 'bg-white dark:bg-gray-800 border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30' },
  ];
};

const EventTimelineCard = ({ message, currentUserId, onUpdated, onEditEvent, onCloneEvent, cloning = false }) => {
  const eventInfo = message?.eventInfo;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showDetail, setShowDetail] = useState(false);
  const [showRsvpModal, setShowRsvpModal] = useState(false);
  const [pendingResponse, setPendingResponse] = useState(null);
  const [rsvpFieldsForModal, setRsvpFieldsForModal] = useState(null);
  const [editingRsvp, setEditingRsvp] = useState(false);
  const [entered, setEntered] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);
  const [followUpSource, setFollowUpSource] = useState(null);
  const [followUpType, setFollowUpType] = useState(null);
  const [followUpPeople, setFollowUpPeople] = useState(null);
  const [showFollowUpWizard, setShowFollowUpWizard] = useState(false);
  const [followUpPollName, setFollowUpPollName] = useState('');
  const hydratedPollRef = useRef(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(t);
  }, [eventInfo?.eventId]);

  useEffect(() => {
    hydratedPollRef.current = false;
  }, [eventInfo?.eventId]);

  useEffect(() => {
    const eventId = eventInfo?.eventId;
    if (!eventId || !isPollEvent(eventInfo) || hydratedPollRef.current) return undefined;
    let cancelled = false;
    hydratedPollRef.current = true;
    fetchEvent(eventId)
      .then((dto) => {
        if (cancelled || !dto) return;
        const nextInfo = eventDtoToTimelineInfo(dto);
        if (!nextInfo || typeof onUpdated !== 'function') return;
        onUpdated({ ...message, eventInfo: { ...(eventInfo || {}), ...nextInfo } });
      })
      .catch(() => {
        hydratedPollRef.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [eventInfo?.eventId]);

  const participants = eventInfo?.participants || [];
  const theme = getTypeTheme(eventInfo?.eventType);
  const typeMeta = getTypeMeta(eventInfo?.eventType);
  const { label: locationLabel } = resolveEventLocation(eventInfo);

  const myParticipant = useMemo(
    () => participants.find((p) => sameId(p.userId, currentUserId)),
    [participants, currentUserId],
  );

  const isCancelled = eventInfo?.status === 'CANCELLED';
  const isClosed = eventInfo?.status === 'CLOSED';
  const responseType = (eventInfo?.responseType || 'NONE').toUpperCase();
  // Channel members can respond even if EVERYONE mode did not expand into participants yet.
  // Backend respondToEvent auto-adds the viewer as a participant.
  const isPoll = isPollEvent(eventInfo);
  const isCreator = sameId(eventInfo?.creatorId, currentUserId);
  const canRespond = !isCancelled
    && !isClosed
    && responseType !== 'NONE'
    && Boolean(eventInfo?.eventId)
    && Boolean(currentUserId);

  if (!eventInfo?.eventId) return null;

  const handleRespond = async (response, rsvpDetails = null) => {
    if (!eventInfo.eventId || saving) return;
    setSaving(true);
    setError('');
    try {
      const payload = { response };
      if (rsvpDetails) payload.rsvpDetails = rsvpDetails;
      const updated = await respondToEvent(eventInfo.eventId, payload);
      const base = eventDtoToTimelineInfo(updated);
      const nextInfo = {
        ...base,
        attachments: (base?.attachments?.length
          ? base.attachments
          : eventInfo.attachments) || [],
        participants: (base?.participants || []).map((p) => {
          const existing = participants.find((x) => sameId(x.userId, p.userId));
          return {
            ...p,
            userName: existing?.userName || p.userName,
            responseDetails: p.responseDetails != null
              ? p.responseDetails
              : existing?.responseDetails,
            selectedPollOptions: Array.isArray(p.selectedPollOptions)
              ? p.selectedPollOptions
              : existing?.selectedPollOptions,
          };
        }),
      };
      if (typeof onUpdated === 'function') {
        onUpdated({ ...message, eventInfo: nextInfo });
      }
      setShowRsvpModal(false);
      setPendingResponse(null);
      setRsvpFieldsForModal(null);
      setEditingRsvp(false);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to submit response');
    } finally {
      setSaving(false);
    }
  };

  const applyUpdatedEvent = (updated) => {
    const base = eventDtoToTimelineInfo(updated);
    const nextInfo = {
      ...base,
      attachments: (base?.attachments?.length
        ? base.attachments
        : eventInfo.attachments) || [],
      participants: (base?.participants || []).map((p) => {
        const existing = participants.find((x) => sameId(x.userId, p.userId));
        return {
          ...p,
          userName: existing?.userName || p.userName,
          responseDetails: p.responseDetails != null
            ? p.responseDetails
            : existing?.responseDetails,
          selectedPollOptions: Array.isArray(p.selectedPollOptions)
            ? p.selectedPollOptions
            : existing?.selectedPollOptions,
        };
      }),
    };
    if (typeof onUpdated === 'function') {
      onUpdated({ ...message, eventInfo: { ...eventInfo, ...nextInfo } });
    }
  };

  const handlePollVote = async (selectedOptions) => {
    if (!eventInfo.eventId || saving) return;
    setSaving(true);
    setError('');
    try {
      const updated = await respondToEvent(eventInfo.eventId, {
        response: selectedOptions.length ? 'VOTE' : 'UNVOTE',
        rsvpDetails: { selectedOptions },
      });
      applyUpdatedEvent(updated);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to vote');
    } finally {
      setSaving(false);
    }
  };

  const handleCloseEvent = async () => {
    if (!eventInfo?.eventId || saving) return;
    setSaving(true);
    setError('');
    try {
      const updated = await closeEvent(eventInfo.eventId);
      const nextInfo = eventDtoToTimelineInfo(updated);
      if (typeof onUpdated === 'function') {
        onUpdated({ ...message, eventInfo: { ...eventInfo, ...nextInfo } });
      }
      setConfirmClose(false);
      setShowDetail(false);
      if (isPoll) {
        setFollowUpSource(updated || eventInfo);
      }
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to close event');
    } finally {
      setSaving(false);
    }
  };

  const handleReopenEvent = async () => {
    if (!eventInfo?.eventId || saving) return;
    setSaving(true);
    setError('');
    try {
      const updated = await reopenEvent(eventInfo.eventId);
      applyUpdatedEvent(updated);
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to reopen poll');
    } finally {
      setSaving(false);
    }
  };

  const resolveRsvpFields = async () => {
    let type = responseType;
    let fields = eventInfo.rsvpFields;
    try {
      const fresh = await fetchEvent(eventInfo.eventId);
      type = String(fresh?.responsePolicy?.type || type || '').toUpperCase();
      fields = fresh?.responsePolicy?.rsvpFields ?? fields;
    } catch {
      // Fall back to timeline card metadata when offline / fetch fails.
    }
    return { type, fields };
  };

  const handleRespondRequest = async (response) => {
    if (response !== 'ACCEPTED') {
      handleRespond(response);
      return;
    }

    const { type, fields } = await resolveRsvpFields();
    if (needsRsvpDetailsOnAccept(type, fields)) {
      setRsvpFieldsForModal(fields);
      setPendingResponse(response);
      setEditingRsvp(false);
      setShowRsvpModal(true);
      return;
    }
    handleRespond(response);
  };

  const handleEditRsvp = async () => {
    if (saving || !eventInfo?.eventId) return;
    setError('');
    const { fields } = await resolveRsvpFields();
    if (!hasEnabledRsvpFields(fields) && !myParticipant?.responseDetails) {
      setError('This invitation has no RSVP questions to edit.');
      return;
    }
    setRsvpFieldsForModal(fields);
    setPendingResponse('ACCEPTED');
    setEditingRsvp(true);
    setShowRsvpModal(true);
  };

  const myStatus = (myParticipant?.status || 'PENDING').toUpperCase();
  const hasVoted = isPoll && selectedPollOptions(myParticipant, eventInfo).length > 0;
  const hasDecided = isPoll
    ? hasVoted
    : (myStatus !== 'PENDING' && myStatus !== 'INVITED');
  const showStatusPill = !(isPoll && String(eventInfo.status || '').toUpperCase() === 'WAITING_FOR_RESPONSE');
  const canEditRsvp = canRespond
    && myStatus === 'ACCEPTED'
    && (
      hasEnabledRsvpFields(eventInfo.rsvpFields)
      || Boolean(myParticipant?.responseDetails && Object.keys(myParticipant.responseDetails).length)
    );
  const responseOptions = isPoll ? [] : getResponseOptions(responseType);
  const scheduleLabel = formatEventSchedule(eventInfo);
  const statusPill = STATUS_PILL[eventInfo.status] || 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200';
  const { images: eventImages, files: eventFiles } = splitEventAttachments(eventInfo.attachments);

  return (
    <>
      <style>{CARD_STYLES}</style>
      <div
        role="button"
        tabIndex={0}
        onClick={() => setShowDetail(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setShowDetail(true);
          }
        }}
        className={`event-timeline-card relative max-w-lg w-full min-w-[300px] text-left overflow-hidden rounded-2xl border shadow-lg hover:shadow-xl hover:scale-[1.01] transition-all duration-200 cursor-pointer ${entered ? 'opacity-100' : 'opacity-0'} ${
          isPoll && hasVoted
            ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30'
            : 'border-gray-200/80 dark:border-gray-600/50 bg-white dark:bg-gray-900'
        }`}
      >
        <div className={`absolute inset-x-0 top-0 h-1 event-timeline-shimmer bg-gradient-to-r ${theme.gradient}`} aria-hidden="true" />

        <div className="relative p-4 space-y-3">
          <div className="flex items-start gap-3">
            <div className={`flex-shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-lg shadow-md`}>
              <span aria-hidden="true">{typeMeta.icon}</span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${theme.badge}`}>
                  {typeMeta.label}
                </span>
                {showStatusPill && (
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${statusPill}`}>
                    {getStatusLabel(eventInfo.status)}
                  </span>
                )}
              </div>
              <h3 className="text-base font-bold text-gray-900 dark:text-white mt-1 leading-snug">
                {eventInfo.name || 'Untitled event'}
              </h3>
            </div>
          </div>

          {!isPoll && (
          <div className="space-y-2 text-xs">
            <div className="flex items-center gap-2 text-gray-700 dark:text-gray-200">
              <span className={`flex-shrink-0 w-7 h-7 rounded-lg bg-gradient-to-br ${theme.gradient} flex items-center justify-center text-white`}>
                <CalendarIcon className="w-3.5 h-3.5" />
              </span>
              <span className="font-medium">{scheduleLabel}</span>
            </div>

            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-rose-500 flex items-center justify-center text-white">
                <LocationIcon className="w-3.5 h-3.5" />
              </span>
              <span className="font-medium truncate">{locationLabel || 'No location set'}</span>
            </div>
          </div>
          )}

          {isPoll && (
            <PollVoteBlock
              eventInfo={eventInfo}
              currentUserId={currentUserId}
              canVote={canRespond}
              saving={saving}
              onVote={handlePollVote}
              compact
            />
          )}
          {isPoll && isCreator && !isClosed && !isCancelled && (
            <button
              type="button"
              disabled={saving}
              onClick={(e) => {
                e.stopPropagation();
                setConfirmClose(true);
              }}
              className="text-xs font-semibold text-rose-600 dark:text-rose-400 hover:underline disabled:opacity-50"
            >
              Close poll
            </button>
          )}
          {isPoll && isCreator && isClosed && (
            <button
              type="button"
              disabled={saving}
              onClick={(e) => {
                e.stopPropagation();
                handleReopenEvent();
              }}
              className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 hover:underline disabled:opacity-50"
            >
              Reopen poll
            </button>
          )}
          {error && isPoll && (
            <p className="text-xs text-rose-600">{error}</p>
          )}

          {isExpenseSharingEvent(eventInfo.eventType) && (
            <SharePersonalAmount
              balances={eventInfo.expenseSummary?.balances}
              userId={currentUserId}
              currency={eventInfo.configuration?.currency || 'INR'}
              size="lg"
            />
          )}

          {(isExpenseTrackingEvent(eventInfo.eventType) || isExpenseSharingEvent(eventInfo.eventType)) && (
            <p className={`text-xs font-semibold ${isExpenseSharingEvent(eventInfo.eventType) ? 'text-amber-800 dark:text-amber-200' : 'text-orange-800 dark:text-orange-200'}`}>
              {(eventInfo.expenseSummary?.entryCount || 0)} entries
              {eventInfo.expenseSummary?.totalAmount != null
                ? ` · total ${eventInfo.expenseSummary.totalAmount}`
                : ''}
              {isClosed ? ' · closed' : ' · tap to add'}
            </p>
          )}

          {eventImages.length > 0 && (
            <EventAttachmentsDisplay
              attachments={eventInfo.attachments}
              imagesOnly
              compact
            />
          )}

          {eventFiles.length > 0 && (
            <p className="text-[10px] text-gray-500 dark:text-gray-400 font-medium">
              {eventFiles.length} attachment{eventFiles.length === 1 ? '' : 's'} · open for download
            </p>
          )}

          {hasDecided && !isPoll && (
            <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-semibold ${PARTICIPANT_STATUS_BADGE[myStatus] || PARTICIPANT_STATUS_BADGE.PENDING}`}>
              {PARTICIPANT_STATUS_LABEL[myStatus] || myStatus}
            </span>
          )}

          <p className="text-[10px] text-primary-600 dark:text-primary-400 font-semibold">Tap to view details →</p>
        </div>
      </div>

      <EventDetailModal
        isOpen={showDetail}
        onClose={() => setShowDetail(false)}
        eventInfo={eventInfo}
        currentUserId={currentUserId}
        saving={saving}
        error={error}
        onRespond={handleRespondRequest}
        onEditRsvp={canEditRsvp ? handleEditRsvp : undefined}
        onEditEvent={onEditEvent}
        onCloneEvent={onCloneEvent}
        onCloseEvent={isPoll ? () => setConfirmClose(true) : handleCloseEvent}
        onReopenEvent={isPoll ? handleReopenEvent : undefined}
        onPollVote={handlePollVote}
        cloning={cloning}
        responseOptions={responseOptions}
        canRespond={canRespond}
        myStatus={myStatus}
        hasDecided={hasDecided}
        myRsvpSummary={formatRsvpSummary(myParticipant?.responseDetails)}
      />

      <RsvpDetailsModal
        isOpen={showRsvpModal}
        onClose={() => {
          if (!saving) {
            setShowRsvpModal(false);
            setPendingResponse(null);
            setRsvpFieldsForModal(null);
            setEditingRsvp(false);
          }
        }}
        onSubmit={(details) => handleRespond(pendingResponse || 'ACCEPTED', details)}
        eventId={eventInfo.eventId}
        userId={currentUserId}
        eventName={eventInfo.name}
        rsvpFields={rsvpFieldsForModal || eventInfo.rsvpFields}
        saving={saving}
        initialDetails={myParticipant?.responseDetails}
        isEditing={editingRsvp}
      />
      <ConfirmModal
        isOpen={confirmClose}
        onClose={() => setConfirmClose(false)}
        onConfirm={handleCloseEvent}
        title={isPoll ? 'Close poll' : 'Close event'}
        message={isPoll ? 'People will not be able to vote after you close this poll.' : 'Close this event?'}
        confirmText="Close"
        cancelText="Cancel"
        type="warning"
      />
      <FollowUpEventPrompt
        isOpen={Boolean(followUpSource) && !showFollowUpWizard}
        title="Poll closed"
        description="Do you want to create another event, like expense sharing, a poll, or a calculation?"
        includePoll
        askParticipants
        onClose={() => setFollowUpSource(null)}
        onContinue={({ eventType, participantMode }) => {
          const people = followUpParticipantsFromPoll(followUpSource, participantMode, currentUserId);
          setFollowUpPollName(pollDisplayName(followUpSource));
          setFollowUpType(eventType);
          setFollowUpPeople(people);
          setFollowUpSource(null);
          setShowFollowUpWizard(true);
        }}
      />
      <CreateEventWizard
        isOpen={showFollowUpWizard}
        channel={message?.channelId ? { id: message.channelId } : undefined}
        personalMode={!message?.channelId}
        initialEventType={followUpType}
        initialParticipants={followUpPeople}
        sourceEventName={followUpPollName}
        skipPostedFollowUp
        onClose={() => {
          setShowFollowUpWizard(false);
          setFollowUpType(null);
          setFollowUpPeople(null);
          setFollowUpPollName('');
        }}
        onCreated={() => {
          setShowFollowUpWizard(false);
          setFollowUpType(null);
          setFollowUpPeople(null);
          setFollowUpPollName('');
        }}
      />
    </>
  );
};

export default EventTimelineCard;
